import React from "react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
} from "recharts";

// Professional muted color palette
export const CHART_COLORS = {
  primary: "#14B8A6",    // Teal (accent)
  secondary: "#6366F1",  // Royal Indigo
  success: "#10B981",    // Emerald
  warning: "#F59E0B",    // Amber
  danger: "#EF4444",     // Red
  info: "#0EA5E9",       // Sky blue
  slate: "#64748B",      // Slate
  pink: "#EC4899",       // Pink
};

export const GRADIENT_COLORS = [
  "#14B8A6",  // Teal
  "#10B981",  // Emerald
  "#6366F1",  // Royal
  "#0EA5E9",  // Sky
  "#8B5CF6",  // Violet
  "#F59E0B",  // Amber
  "#EC4899",  // Pink
  "#64748B",  // Slate
];

// Custom Tooltip Component with glassmorphism
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass-strong rounded-xl p-3 shadow-glass-lg border border-white/20">
        <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-semibold">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Chart Card Wrapper with glassmorphism
export const ChartCard = ({ title, subtitle, children, className = "", action }) => (
  <div className={`ca-card overflow-hidden ${className}`}>
    <div className="px-6 py-4 border-b border-neutral-200/30 dark:border-white/10 flex items-center justify-between">
      <div>
        <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100">{title}</h3>
        {subtitle && <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

// Line Chart Component
export const SimpleLineChart = ({ data, dataKey, xAxisKey = "name", color = CHART_COLORS.primary, height = 300 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
      <defs>
        <linearGradient id={`lineGradient-${dataKey}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={CHART_COLORS.primary} />
          <stop offset="100%" stopColor={CHART_COLORS.secondary} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
      <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} stroke="#9CA3AF" />
      <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
      <Tooltip content={<CustomTooltip />} />
      <Line
        type="monotone"
        dataKey={dataKey}
        stroke={`url(#lineGradient-${dataKey})`}
        strokeWidth={3}
        dot={{ fill: color, strokeWidth: 2, r: 4 }}
        activeDot={{ r: 6, fill: CHART_COLORS.secondary }}
      />
    </LineChart>
  </ResponsiveContainer>
);

// Area Chart Component
export const SimpleAreaChart = ({ data, dataKey, xAxisKey = "name", color = CHART_COLORS.primary, height = 300 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
      <defs>
        <linearGradient id={`areaGradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor={color} stopOpacity={0.3} />
          <stop offset="95%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
      <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} stroke="#9CA3AF" />
      <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
      <Tooltip content={<CustomTooltip />} />
      <Area
        type="monotone"
        dataKey={dataKey}
        stroke={color}
        strokeWidth={2}
        fill={`url(#areaGradient-${dataKey})`}
      />
    </AreaChart>
  </ResponsiveContainer>
);

// Multi-Line Area Chart
export const MultiAreaChart = ({ data, lines, xAxisKey = "name", height = 300 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
      <defs>
        {lines.map((line, idx) => (
          <linearGradient key={line.dataKey} id={`gradient-${line.dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={line.color || GRADIENT_COLORS[idx]} stopOpacity={0.3} />
            <stop offset="95%" stopColor={line.color || GRADIENT_COLORS[idx]} stopOpacity={0} />
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
      <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} stroke="#9CA3AF" />
      <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
      <Tooltip content={<CustomTooltip />} />
      <Legend />
      {lines.map((line, idx) => (
        <Area
          key={line.dataKey}
          type="monotone"
          dataKey={line.dataKey}
          name={line.name || line.dataKey}
          stroke={line.color || GRADIENT_COLORS[idx]}
          strokeWidth={2}
          fill={`url(#gradient-${line.dataKey})`}
        />
      ))}
    </AreaChart>
  </ResponsiveContainer>
);

// Bar Chart Component
export const SimpleBarChart = ({ data, dataKey, xAxisKey = "name", color = CHART_COLORS.primary, height = 300 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
      <defs>
        <linearGradient id={`barGradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={CHART_COLORS.primary} />
          <stop offset="100%" stopColor={CHART_COLORS.secondary} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
      <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} stroke="#9CA3AF" />
      <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
      <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(59, 130, 246, 0.1)" }} />
      <Bar
        dataKey={dataKey}
        fill={`url(#barGradient-${dataKey})`}
        radius={[6, 6, 0, 0]}
        maxBarSize={50}
      />
    </BarChart>
  </ResponsiveContainer>
);

// Stacked Bar Chart
export const StackedBarChart = ({ data, bars, xAxisKey = "name", height = 300 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
      <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} stroke="#9CA3AF" />
      <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
      <Tooltip content={<CustomTooltip />} />
      <Legend />
      {bars.map((bar, idx) => (
        <Bar
          key={bar.dataKey}
          dataKey={bar.dataKey}
          name={bar.name || bar.dataKey}
          stackId="stack"
          fill={bar.color || GRADIENT_COLORS[idx]}
          radius={idx === bars.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
        />
      ))}
    </BarChart>
  </ResponsiveContainer>
);

// Pie Chart Component
export const SimplePieChart = ({ data, height = 300, innerRadius = 0, showLabels = true }) => {
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return percent > 0.05 ? (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    ) : null;
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={showLabels ? renderCustomizedLabel : false}
          outerRadius={height / 3}
          innerRadius={innerRadius}
          dataKey="value"
          animationBegin={0}
          animationDuration={1000}
        >
          {data.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={entry.color || GRADIENT_COLORS[index % GRADIENT_COLORS.length]}
              stroke="white"
              strokeWidth={2}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          verticalAlign="bottom" 
          height={36}
          formatter={(value) => <span className="text-sm text-gray-700">{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

// Donut Chart Component
export const DonutChart = ({ data, height = 300, centerLabel, centerValue }) => (
  <div className="relative">
    <SimplePieChart data={data} height={height} innerRadius={height / 4.5} showLabels={false} />
    {centerLabel && (
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-3xl font-bold text-gray-900">{centerValue}</span>
        <span className="text-sm text-gray-500">{centerLabel}</span>
      </div>
    )}
  </div>
);

// Radial Progress Chart
export const RadialProgressChart = ({ data, height = 250 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <RadialBarChart 
      cx="50%" 
      cy="50%" 
      innerRadius="30%" 
      outerRadius="100%" 
      barSize={12} 
      data={data}
      startAngle={180}
      endAngle={0}
    >
      <RadialBar
        minAngle={15}
        background={{ fill: "#F3F4F6" }}
        clockWise
        dataKey="value"
        cornerRadius={10}
      />
      <Legend 
        iconSize={10} 
        layout="horizontal" 
        verticalAlign="bottom"
        formatter={(value) => <span className="text-sm text-gray-700">{value}</span>}
      />
      <Tooltip content={<CustomTooltip />} />
    </RadialBarChart>
  </ResponsiveContainer>
);

// Stat Card with mini chart - Glassmorphism style
export const StatCard = ({ title, value, change, changeType = "increase", icon, color = "teal", sparkData }) => {
  const colorClasses = {
    teal: "from-accent-500 to-emerald-500",
    purple: "from-royal-500 to-violet-500",
    green: "from-emerald-500 to-green-500",
    orange: "from-amber-500 to-orange-500",
    red: "from-red-500 to-rose-500",
    cyan: "from-cyan-500 to-sky-500",
    blue: "from-accent-500 to-emerald-500", // Map blue to accent for backwards compat
  };

  const sparkColorMap = {
    teal: "#14B8A6",
    purple: "#6366F1",
    green: "#10B981",
    orange: "#F59E0B",
    red: "#EF4444",
    cyan: "#06B6D4",
    blue: "#14B8A6",
  };

  return (
    <div className="ca-card p-6 relative overflow-hidden group hover:shadow-glass-lg transition-all duration-300 card-lift">
      {/* Background decoration */}
      <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${colorClasses[color] || colorClasses.teal} opacity-10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500`}></div>
      
      <div className="flex items-start justify-between relative z-10">
        <div>
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{title}</p>
          <p className="text-3xl font-bold text-neutral-800 dark:text-neutral-100 mt-2">{value}</p>
          {change && (
            <div className={`flex items-center gap-1 mt-2 text-sm font-medium ${changeType === "increase" ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"}`}>
              <i className={`ri-arrow-${changeType === "increase" ? "up" : "down"}-line`}></i>
              {change}
            </div>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color] || colorClasses.teal} flex items-center justify-center text-white shadow-lg`}>
          <i className={`${icon} text-xl`}></i>
        </div>
      </div>

      {/* Mini sparkline */}
      {sparkData && (
        <div className="mt-4 h-12">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkData}>
              <defs>
                <linearGradient id={`spark-${title}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={sparkColorMap[color] || sparkColorMap.teal} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={sparkColorMap[color] || sparkColorMap.teal} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke={sparkColorMap[color] || sparkColorMap.teal}
                strokeWidth={2}
                fill={`url(#spark-${title})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// Progress Ring Component - Updated colors
export const ProgressRing = ({ progress, size = 120, strokeWidth = 10, color = CHART_COLORS.primary, label }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(0,0,0,0.05)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="none"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-neutral-800 dark:text-neutral-100">{progress}%</span>
        {label && <span className="text-xs text-neutral-500 dark:text-neutral-400">{label}</span>}
      </div>
    </div>
  );
};

export default {
  ChartCard,
  SimpleLineChart,
  SimpleAreaChart,
  MultiAreaChart,
  SimpleBarChart,
  StackedBarChart,
  SimplePieChart,
  DonutChart,
  RadialProgressChart,
  StatCard,
  ProgressRing,
  CHART_COLORS,
  GRADIENT_COLORS,
};
